import { holds } from './codm.js';
/** Whether this viewer is sent every party of the campaign, and every character placed in one. */
export const seesEveryParty = (who) => holds(who, 'tokens') || holds(who, 'see_hidden');
/** The ids of the parties this viewer sits in, read from the members list: a player's own seats; nobody else has any. */
function seatsOf(members, viewer) {
    const me = members.find((m) => m.id === viewer.id && m.role === 'player');
    return new Set((me?.parties ?? []).map((p) => p.id));
}
/**
 * The members list as this viewer is sent it. Everyone at the table is in it, as before (they are the campaign's
 * people, issue 1577); what a player is not sent is any party they do not sit in: a member's parties are cut to the
 * viewer's, and a character is kept only when it is placed in one of the viewer's parties (or is the viewer's own),
 * carrying the viewer's parties alone. An empty list of parties is no key at all.
 */
export function membersFor(members, viewer) {
    if (seesEveryParty(viewer))
        return members.map((m) => ({ ...m }));
    // A watcher: the list as it was before issue 1579, every character by id and name, and no party anywhere in it.
    if (viewer.role === 'watcher')
        return members.map(({ parties: _p, characters, ...rest }) => ({ ...rest, characters: characters.map(({ id, name }) => ({ id, name })) }));
    const mine = seatsOf(members, viewer);
    const keep = (ps) => (ps ?? []).filter((p) => mine.has(p.id));
    return members.map(({ parties, characters, ...rest }) => {
        const ps = keep(parties);
        const chars = characters
            .filter((c) => rest.id === viewer.id || keep(c.parties).length > 0)
            .map(({ parties: cp, ...c }) => { const kept = keep(cp); return kept.length ? { ...c, parties: kept } : c; });
        return { ...rest, characters: chars, ...(ps.length ? { parties: ps } : {}) };
    });
}
/** The campaign's parties (in the site's order) as this viewer is sent them: every one, or the viewer's seats alone. */
export function partiesFor(all, members, viewer) {
    if (seesEveryParty(viewer))
        return all.map((p) => ({ ...p }));
    const mine = seatsOf(members, viewer);
    return all.filter((p) => mine.has(p.id)).map((p) => ({ ...p }));
}
/**
 * Things grouped under their parties, in the parties' order, a thing in two parties under each; a party with none is
 * no group. With one party or none known there are no groups to show: one group, `party` null, everything in it, as
 * the table looked before parties were groups at it. A thing whose parties are none of those known closes the list,
 * `party` null.
 */
export function byParty(items, order) {
    if (order.length <= 1)
        return [{ party: null, items: [...items] }];
    const groups = order.map((party) => ({ party, items: items.filter((i) => i.parties?.some((p) => p.id === party.id)) })).filter((g) => g.items.length > 0);
    const known = new Set(order.map((p) => p.id));
    const rest = items.filter((i) => !i.parties?.some((p) => known.has(p.id)));
    return rest.length ? [...groups, { party: null, items: rest }] : groups;
}
/** The parties as one line, "Blue Hand · Red Hand"; empty for none. */
export const partyLine = (parties) => (parties ?? []).map((p) => p.name).join(' · ');
/** A character token's party as its Properties says it: "Party: Blue Hand", "Parties: Blue Hand, Red Hand"; null for none. */
export function partyWords(parties) {
    const names = (parties ?? []).map((p) => p.name);
    if (names.length === 0)
        return null;
    return `${names.length === 1 ? 'Party' : 'Parties'}: ${names.join(', ')}`;
}
//# sourceMappingURL=parties.js.map